import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { legalConfig } from '../config/legal';

const VALID_PAGES = ['privacy', 'terms', 'cookies'] as const;
export type LegalPageKey = (typeof VALID_PAGES)[number];

function isLegalPageKey(value: string | undefined): value is LegalPageKey {
  return !!value && (VALID_PAGES as readonly string[]).includes(value);
}

type LegalPageContentProps = {
  page: LegalPageKey;
  embedded?: boolean;
};

export function LegalPageContent({ page, embedded = false }: LegalPageContentProps) {
  const { t } = useTranslation();
  const bullets = t(`legal.pages.${page}.bullets`, { returnObjects: true }) as string[];

  return (
    <div className={embedded ? '' : 'max-w-3xl mx-auto px-4 py-12 sm:py-16'}>
      {!embedded && (
        <>
          <p className="text-xs uppercase tracking-widest text-textSecondary mb-2">{t('legal.badge')}</p>
          <h1 className="text-3xl font-semibold text-textPrimary mb-4">{t(`legal.pages.${page}.title`)}</h1>
        </>
      )}
      <p className="text-textSecondary leading-relaxed mb-6">{t(`legal.pages.${page}.intro`)}</p>

      <ul className="list-disc pl-5 space-y-2 text-textSecondary mb-8">
        {Array.isArray(bullets) && bullets.map((item) => <li key={item}>{item}</li>)}
      </ul>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-textSecondary">
        <p>{t('legal.pages.contact')}</p>
        <p className="mt-2">
          <a href={`mailto:${legalConfig.privacyContactEmail}`} className="fo-link">
            {legalConfig.privacyContactEmail}
          </a>
        </p>
        <p className="mt-4 text-xs">{t('legal.pages.disclaimer')}</p>
      </div>

      {!embedded && (
        <Link to="/" className="fo-link inline-block mt-8">
          {t('legal.backHome')}
        </Link>
      )}
    </div>
  );
}

export function LegalInfoPage() {
  const { page } = useParams<{ page: string }>();
  const { t } = useTranslation();

  if (!isLegalPageKey(page)) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <p className="text-textSecondary">{t('legal.pageNotFound')}</p>
        <Link to="/" className="fo-link mt-4 inline-block">
          {t('legal.backHome')}
        </Link>
      </div>
    );
  }

  return <LegalPageContent page={page} />;
}
